import React from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { useLocation, useNavigate } from 'react-router-dom';
import Paper from '@material-ui/core/Paper';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import ListSubheader from '@material-ui/core/ListSubheader';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';

import { useEnv } from './EnvContext';
import { findApp, urlsForApp } from '../coalitions';

// Env + App selector bar for any config-driven coalition. Coalition
// lookup + apps list + tier assignment all come from the resolved
// coalition — no hardcoding.
const useStyles = makeStyles(theme => ({
  bar: {
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  select: { minWidth: 180 },
  clusterHint: {
    marginLeft: 'auto',
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  code: {
    background: theme.palette.type === 'dark' ? '#2a2a2a' : '#f3f4f6',
    padding: '2px 6px',
    borderRadius: 3,
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: 12,
  },
}));

export const EntitySparrowDeskSelectorBar = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const config = useApi(configApiRef);
  const navigate = useNavigate();
  const location = useLocation();

  const { coalition, env, tier, setEnv } = useEnv(entity, config);

  if (!coalition) return null;

  const currentApp = entity.metadata.name;

  const onAppChange = (nextApp: string) => {
    if (nextApp === currentApp) return;
    // Preserve tab sub-route (/jenkins, /deployments, …) + ?env= when
    // hopping to another Component.
    const kind = (entity.kind || 'component').toLowerCase();
    const namespace = (entity.metadata.namespace || 'default').toLowerCase();
    const subroute = location.pathname.split('/').slice(5).join('/');
    const suffix = subroute ? `/${subroute}` : '';
    navigate(`/catalog/${namespace}/${kind}/${nextApp}${suffix}?env=${env}`);
  };

  const tierPill = tier === 'production'
    ? { label: 'production', bg: '#dbeafe', fg: '#1e40af' }
    : { label: 'staging', bg: '#fef3c7', fg: '#92400e' };

  const app = findApp(coalition, entity);
  const linkSet = app ? urlsForApp(app, env, coalition) : undefined;
  const displayCluster = linkSet?.cluster ?? '—';
  const displayNamespace = linkSet?.namespace ?? env;

  const stagingEnvs = coalition.staging.envs;
  const productionEnvs = coalition.production.envs;

  return (
    <Paper variant="outlined" className={classes.bar}>
      <FormControl variant="outlined" size="small" className={classes.select}>
        <InputLabel id="env-dashboard-env-label">Env</InputLabel>
        <Select
          labelId="env-dashboard-env-label"
          label="Env"
          value={env}
          onChange={e => setEnv(e.target.value as string)}
        >
          {stagingEnvs.length > 0 && (
            <ListSubheader disableSticky>Staging ({stagingEnvs.length})</ListSubheader>
          )}
          {stagingEnvs.map(e => (
            <MenuItem key={`staging-${e}`} value={e}>{e}</MenuItem>
          ))}
          {productionEnvs.length > 0 && (
            <ListSubheader disableSticky>Production ({productionEnvs.length})</ListSubheader>
          )}
          {productionEnvs.map(e => (
            <MenuItem key={`prod-${e}`} value={e}>{e}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Chip
        size="small"
        label={tierPill.label}
        style={{ background: tierPill.bg, color: tierPill.fg }}
      />

      {/* Single-app coalitions (each website is its own coalition) don't
          need an App switcher — the entity IS the app. Only render the
          dropdown when there are 2+ apps to switch between. */}
      {coalition.apps.length > 1 && (
        <FormControl variant="outlined" size="small" className={classes.select}>
          <InputLabel id="env-dashboard-app-label">App</InputLabel>
          <Select
            labelId="env-dashboard-app-label"
            label="App"
            value={coalition.apps.some(a => a.name === currentApp) ? currentApp : ''}
            onChange={e => onAppChange(e.target.value as string)}
          >
            {coalition.apps.map(a => (
              <MenuItem key={a.name} value={a.name}>{a.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Typography className={classes.clusterHint}>
        Cluster <span className={classes.code}>{displayCluster}</span>
        {'  ·  Namespace '}
        <span className={classes.code}>{displayNamespace}</span>
      </Typography>
    </Paper>
  );
};

export default EntitySparrowDeskSelectorBar;
